import { useState, useEffect } from "react";
import axios from "axios";
import { Image, Form, Pagination, Spinner, Card, Row, Col, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiSearch } from "react-icons/fi";
import { BsGrid3X3Gap, BsListUl } from "react-icons/bs";
import "./Products.css"; // Create this CSS file for custom styles

function KitchensEssentials() {
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchField, setSearchField] = useState("name");
    const [currentPage, setCurrentPage] = useState(1);
    const [productsPerPage] = useState(12);
    const [isLoading, setIsLoading] = useState(true);
    const [viewMode, setViewMode] = useState("grid"); // 'grid' or 'list'

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                setIsLoading(true);
                const response = await axios.get(
                    `${process.env.REACT_APP_API_BASE}/products/kitchens-Zulek-Essentials`
                );
                setProducts(response.data);
                setFilteredProducts(response.data);
                toast.success("Products loaded successfully");
            } catch (err) {
                console.error(err);
                toast.error("Failed to load products");
            } finally {
                setIsLoading(false);
            }
        };
        fetchProducts();
    }, []);

    useEffect(() => {
        filterProducts();
        // eslint-disable-next-line 
    }, [products, searchTerm, searchField]);

    const filterProducts = () => {
        const filtered = products.filter(product => {
            if (!searchTerm) return true;
            const fieldValue = String(product[searchField]).toLowerCase();
            return fieldValue.includes(searchTerm.toLowerCase());
        });
        setFilteredProducts(filtered);
        setCurrentPage(1);
    };

    // Pagination logic
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
    const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleRefresh = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE}/products/`
            );
            setProducts(response.data);
            setFilteredProducts(response.data);
            setSearchTerm("");
            toast.success("Products refreshed successfully");
        } catch (err) {
            console.error(err);
            toast.error("Failed to refresh products");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="products-container">
            <Card className="shadow-sm mb-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h4 className="mb-0 text-primary">
                                <strong>Essential Kitchens</strong>
                            </h4>
                            <small className="text-muted">
                                {filteredProducts.length} products available
                            </small>
                        </div>

                        <div className="d-flex align-items-center">
                            <div className="view-toggle me-3">
                                <button
                                    className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setViewMode('grid')}
                                >
                                    <BsGrid3X3Gap />
                                </button>
                                <button
                                    className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
                                    onClick={() => setViewMode('list')}
                                >
                                    <BsListUl />
                                </button>
                            </div>

                            <button
                                className="btn btn-outline-secondary me-3"
                                onClick={handleRefresh}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Refreshing...' : 'Refresh'}
                            </button>
                        </div>
                    </div>

                    <div className="search-filter-container mb-4">
                        <div className="search-box">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <FiSearch />
                                </span>
                                <Form.Control
                                    type="text"
                                    placeholder={`Search by ${searchField}...`}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <Form.Select
                                    value={searchField}
                                    onChange={(e) => setSearchField(e.target.value)}
                                    style={{ maxWidth: '150px' }}
                                >
                                    <option value="name">Name</option>
                                    <option value="sku">SKU</option>
                                    <option value="product_id">ID</option>
                                </Form.Select>
                            </div>
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-2">Loading products...</p>
                        </div>
                    ) : (
                        <>
                            {viewMode === 'grid' ? (
                                <Row className="g-4">
                                    {currentProducts.length > 0 ? (
                                        currentProducts.map((product) => (
                                            <Col key={product.product_id} xs={12} sm={6} md={4} lg={3}>
                                                <Card className="h-100 product-card">
                                                    {product.images?.length > 0 ? (
                                                        <div className="product-image-container">
                                                            <Image
                                                                src={`https://zulekdesigns.com/${product.images[0]}`}
                                                                alt={product.name}
                                                                className="product-image"
                                                                fluid
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="product-image-placeholder">
                                                            <span>No Image</span>
                                                        </div>
                                                    )}
                                                    <Card.Body>
                                                        <Card.Title className="product-title">
                                                            {product.name}
                                                        </Card.Title>
                                                        <Card.Text className="text-muted product-sku">
                                                            SKU: {product.sku}
                                                        </Card.Text>
                                                        <Card.Text className="product-description">
                                                            {product.description.length > 100
                                                                ? `${product.description.substring(0, 100)}...`
                                                                : product.description}
                                                        </Card.Text>
                                                    </Card.Body>
                                                    <Card.Footer className="bg-transparent">
                                                        <small className="text-muted">ID: {product.product_id}</small>
                                                    </Card.Footer>
                                                    <Card.Footer className="bg-transparent">
                                                        <Button
                                                            variant="linkoutline-primary"
                                                            onClick={() => window.open(`https://zulekdesigns.com/${product.images[0]}`, '_blank')}
                                                            className="p-0 text-primary"
                                                        >
                                                            Open Image
                                                        </Button>
                                                    </Card.Footer>
                                                </Card>
                                            </Col>
                                        ))
                                    ) : (
                                        <Col xs={12}>
                                            <div className="text-center py-5">
                                                <h5>No products found</h5>
                                                <p>Try adjusting your search criteria</p>
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                <th>ID</th>
                                                <th>Image</th>
                                                <th>SKU</th>
                                                <th>Name</th>
                                                <th>Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentProducts.length > 0 ? (
                                                currentProducts.map((product) => (
                                                    <tr key={product.product_id}>
                                                        <td>{product.product_id}</td>
                                                        <td>
                                                            {product.images?.length > 0 ? (
                                                                <Image
                                                                    src={`https://zulekdesigns.com/${product.images[0]}`}
                                                                    alt={product.name}
                                                                    width={80}
                                                                    rounded
                                                                />
                                                            ) : (
                                                                <span className="text-muted">No image</span>
                                                            )}
                                                        </td>
                                                        <td>{product.sku}</td>
                                                        <td>
                                                            <strong>{product.name}</strong>
                                                        </td>
                                                        <td>
                                                            <div className="text-truncate" style={{ maxWidth: '300px' }}>
                                                                {product.description}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4">
                                                        <h5>No products found</h5>
                                                        <p>Try adjusting your search criteria</p>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {filteredProducts.length > productsPerPage && (
                                <div className="d-flex justify-content-center mt-4">
                                    <Pagination>
                                        <Pagination.First
                                            onClick={() => paginate(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev
                                            onClick={() => paginate(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />

                                        {[...Array(totalPages)].map((_, index) => (
                                            <Pagination.Item
                                                key={index + 1}
                                                active={index + 1 === currentPage}
                                                onClick={() => paginate(index + 1)}
                                            >
                                                {index + 1}
                                            </Pagination.Item>
                                        ))}

                                        <Pagination.Next
                                            onClick={() => paginate(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last
                                            onClick={() => paginate(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}

export default KitchensEssentials;